export type ColumnType = 'number' | 'string';

/**
 * Shared header-name heuristics for file-upload/payapp flows.
 * Keep these centralized so changes remain consistent across components.
 */
export const ALWAYS_STRING_HEADER_NAMES = ['description'] as const;

export const NUMERIC_HEADER_KEYWORDS = [
  'budget',
  'cost',
  'total',
  'amount',
  'retainage',
  'complete',
  'stored',
  'quantity',
  'price',
] as const;

const normalizeHeader = (header: string) => header.trim().toLowerCase();

export const isAlwaysStringHeaderName = (headerName: string) => {
  const h = normalizeHeader(headerName);
  return (ALWAYS_STRING_HEADER_NAMES as readonly string[]).includes(h);
};

export const isNumericHeaderName = (headerName: string) => {
  const h = normalizeHeader(headerName);
  return (NUMERIC_HEADER_KEYWORDS as readonly string[]).some(keyword => h.includes(keyword));
};

/**
 * Attempts to parse currency/number-like values commonly found in spreadsheets:
 * - "$1,234.56"
 * - "($1,234.56)" accounting negative format
 * - "1,234" / "1234"
 *
 * Returns null if the value isn't parseable as a number.
 */
export const tryParseCurrencyLike = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Detect negative accounting format (e.g. ($1,234.56) or $(1,234.56))
  const isNegative = /^\(\s*\$?.*?\s*\)$/.test(trimmed) || /^\$\s*\(.*?\)$/.test(trimmed);

  // Remove currency symbols, commas, whitespace, parentheses
  const cleaned = trimmed.replace(/[$,()\s]/g, '');
  if (!cleaned) return null;

  const parsed = parseFloat(cleaned);
  if (Number.isNaN(parsed)) return null;

  return isNegative ? -parsed : parsed;
};

export const isNumericLike = (value: unknown) => tryParseCurrencyLike(value) !== null;

export const getColumnTypeFromKey = (
  columnKey: string,
  fieldHeadersMapping: Record<string, string>,
  rows: Array<Record<string, any>>,
  opts?: { sampleSize?: number; numericThreshold?: number },
): ColumnType => {
  const headerName = fieldHeadersMapping[columnKey] || columnKey;

  if (isAlwaysStringHeaderName(headerName)) {
    return 'string';
  }

  const numericByHeader = isNumericHeaderName(headerName);

  // Check actual data in the column (sample a few rows)
  const sampleSize = opts?.sampleSize ?? 10;
  const numericThreshold = opts?.numericThreshold ?? 0.7;

  if (rows.length > 0) {
    const sampleValues = rows
      .slice(0, Math.min(sampleSize, rows.length))
      .map(row => row[columnKey])
      .filter(val => val !== null && val !== undefined && val !== '');

    if (sampleValues.length > 0) {
      const numericCount = sampleValues.filter(isNumericLike).length;
      if (numericCount / sampleValues.length >= numericThreshold) {
        return 'number';
      }
    }
  }

  return numericByHeader ? 'number' : 'string';
};
